// src/pages/Advertisement/Advertisement.jsx
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, Grid, CardActionArea, CircularProgress } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRectangleAd,
  faWebAwesome,
  faStar,
  faWifi,
  faStop,
  faExchangeAlt,
  faCalculator
} from '@fortawesome/free-solid-svg-icons';
import { usePermissions } from '../../context/PermissionContext';
import BackButton from '../../components/BackButton';

// Define the Advertisement options
const advertisementOptions = [
  {
    title: 'Create AD PROVIDER',
    icon: faRectangleAd,
    path: '/ad/ad-providers',
    perm: 'ads.providers',
    description: 'Manage advertisement provider settings'
  },
  {
    title: 'Create Fullpage Ad',
    icon: faWebAwesome,
    path: '/ad/fullpage-ad',
    perm: 'ads.fullpage',
    description: 'Design and publish fullpage advertisements'
  },
  {
    title: 'Create Banner Ad',
    icon: faStar,
    path: '/ad/banner-ad',
    perm: 'ads.banner',
    description: 'Create banner advertisements'
  },
  {
    title: 'Live Ads',
    icon: faWifi,
    path: '/ad/live-ads',
    perm: 'ads.live',
    description: 'View and manage active advertisements'
  },
  {
    title: 'Non Live Ads',
    icon: faStop,
    path: '/ad/non-live-ads',
    perm: 'ads.nonLive',
    description: 'Manage paused or scheduled advertisements'
  },
  {
    title: 'Advertisement Request',
    icon: faExchangeAlt,
    path: '/advertisement/ad-requests',
    perm: 'ads.requests',
    description: 'View and verify advertisement requests from Customers and Merchants (with history)'
  },
  {
    title: 'Ad Calculator',
    icon: faCalculator,
    path: '/advertisement/ad-calculator',
    perm: 'ads.calculator',
    description: 'Calculate advertisement pricing based on ad type, target pincodes, constituencies, or districts'
  }
];

const Advertisement = () => {
  const { can, loading } = usePermissions();
  const navigate = useNavigate();

  const handleCardClick = useCallback((path) => {
    navigate(path);
  }, [navigate]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <CircularProgress />
      </Grid>
    );
  }

  // Check if user has access to any of the cards
  const hasAnyAccess = advertisementOptions.some(option => can(option.perm));

  if (!hasAnyAccess) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '50vh' }}>
        <Typography variant="h6" color="error">
          You don't have permission to access advertisement features. Please contact your administrator.
        </Typography>
      </Grid>
    );
  }

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br/><br/>
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 4 ,mt:-7}}>
        Advertisement Management
      </Typography>
      
      <Grid container spacing={3} justifyContent="center" alignItems="center" sx={{ padding: 2 }}>
        {advertisementOptions.map((option) => (
          can(option.perm) && (
            <Grid item xs={12} sm={6} md={4} lg={3} key={option.title}>
              <Card
                elevation={3}
                sx={{
                  maxWidth: 225,
                  height: '100%',
                  backgroundColor: 'white',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 10px 20px rgba(0,0,0,0.2)',
                    '& .MuiTypography-root': { color: 'white' },
                    '& .fa-icon': { color: 'white' },
                  },
                }}
              >
                <CardActionArea 
                  onClick={() => handleCardClick(option.path)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                  <CardContent sx={{ textAlign: 'center', padding: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <FontAwesomeIcon
                      icon={option.icon}
                      size="5x"
                      className="fa-icon"
                      color="#FFA500"
                      style={{ marginBottom: '16px' }}
                    />
                    <Typography 
                      gutterBottom 
                      variant="h5" 
                      component="div" 
                      sx={{ fontWeight: 500 }}
                    >
                      {option.title}
                    </Typography>
                    <Typography 
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, fontSize: '0.85rem' }}
                    >
                      {option.description}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        ))}
      </Grid>
    </Box>
  );
};

export default Advertisement;